import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/products";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useProducts = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAllProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("/");
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/${id}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createProduct = useCallback(async (productData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post("/", productData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProduct = useCallback(async (id, productData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/${id}`, productData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProduct = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin functions
  const getProductsByCompanyId = useCallback(async (companyId) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/admin/${companyId}`);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createProductForCompany = useCallback(async (companyId, productData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post(`/admin/${companyId}`, productData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProductForCompany = useCallback(async (companyId, id, productData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/admin/${companyId}/${id}`, productData);
      return data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProductForCompany = useCallback(async (companyId, id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/admin/${companyId}/${id}`);
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getAllProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    getProductsByCompanyId,
    createProductForCompany,
    updateProductForCompany,
    deleteProductForCompany,
  };
};

export default useProducts;
